import type {
    DocuWareInvoiceListResult, DocuWareInvoiceRow, DocuWareCompanySummary,
    DocuWareFacets, DocuWareInvoiceFilters, DocuWareSortField,
} from '../types/docuwareInvoices';

interface ListParams {
    page: number;
    limit: number;
    filters: DocuWareInvoiceFilters;
    sortBy: DocuWareSortField;
    sortOrder: 'asc' | 'desc';
}

const EMPTY_FACETS: DocuWareFacets = {
    documentType: [], invoiceCategory: [], workflowStatus: [], currency: [],
};

/**
 * Why one section could not be shown. Carries the HTTP status and the endpoint's own `code`
 * because the window reacts differently to them: a 415 (this section is not a PDF) leaves the
 * other sections worth trying, a 404 NO_SUCH_SECTION or a 502 does not.
 */
export class DocuWarePdfError extends Error {
    readonly status: number;
    readonly code: string | null;

    constructor(message: string, status: number, code: string | null) {
        super(message);
        this.name = 'DocuWarePdfError';
        this.status = status;
        this.code = code;
    }
}

/**
 * One section as the endpoint delivered it: a PDF, or (only when `render: 'html'` was asked
 * for) the sanitised HTML view of an XML e-invoice.
 */
export interface DocuWareSectionContent {
    blob: Blob;
    contentType: 'application/pdf' | 'text/html';
}

export const docuwareInvoicesApi = {
    /**
     * Raw fetch rather than fetchApi, per house rule: the pagination wrapper carries the
     * total and fetchApi unwraps only `data`.
     */
    async list(params: ListParams): Promise<DocuWareInvoiceListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);

        // Every filter is a plain string on the wire; empty and 'all' are simply omitted so
        // the URL shows what is actually narrowing the result.
        for (const [key, value] of Object.entries(params.filters)) {
            if (value === undefined || value === null || value === '') continue;
            if (key === 'postingStatus' && value === 'all') continue;
            sp.set(key, String(value));
        }

        const response = await fetch(`/api/v1/docuware-invoices?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();
        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }
        return {
            items: (body.data?.items ?? []) as DocuWareInvoiceRow[],
            companies: (body.data?.companies ?? []) as DocuWareCompanySummary[],
            facets: { ...EMPTY_FACETS, ...(body.data?.facets ?? {}) } as DocuWareFacets,
            lastImport: (body.data?.lastImport ?? null) as string | null,
            lastImportCompany: (body.data?.lastImportCompany ?? null) as string | null,
            total: body.pagination?.total ?? 0,
        };
    },

    /**
     * One section of one row, as bytes. `id` is `[Reporting].[DocuWareDocument].Id` — the row id
     * the list already carries; cabinet and DocId are the server's business and never travel.
     *
     * A plain `fetch`, not `fetchApi`: success is bytes, failure is the JSON envelope, and the
     * envelope's sentence ("this document is no longer in DocuWare") is the only useful part of
     * the answer, so it is surfaced instead of "Request failed: 404".
     *
     * `application/pdf` is always a success. `text/html` is a success only when the caller passed
     * `render: 'html'` (the XML e-invoice view of the endpoint, plan C1x): without the parameter
     * the endpoint never answers HTML, so an HTML body there is the SWA gateway and stays an
     * error. The HTML blob is typed `text/html` and is meant for a `sandbox=""` iframe only.
     */
    async fetchSection(
        id: string,
        section: number,
        signal?: AbortSignal,
        render?: 'html'
    ): Promise<DocuWareSectionContent> {
        const q = new URLSearchParams({ id, section: String(section) });
        if (render) q.set('render', render);
        let res: Response;
        try {
            res = await fetch(`/api/v1/docuware-invoices/document-pdf?${q}`, { signal });
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') throw err;
            throw new DocuWarePdfError('The document could not be fetched. Please check the connection and try again.', 0, null);
        }
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/pdf')) {
            return { blob: await res.blob(), contentType: 'application/pdf' };
        }
        if (res.ok && render === 'html' && contentType.includes('text/html')) {
            const bytes = await res.arrayBuffer();
            return { blob: new Blob([bytes], { type: 'text/html;charset=utf-8' }), contentType: 'text/html' };
        }

        if (contentType.includes('application/json')) {
            const body = await res.json().catch(() => null);
            const message = body?.error?.message;
            const code = body?.error?.code;
            if (typeof message === 'string' && message) {
                throw new DocuWarePdfError(message, res.status, typeof code === 'string' ? code : null);
            }
        }
        // An HTML body here is the SWA gateway, not our endpoint — usually a timeout.
        throw new DocuWarePdfError(
            res.status === 504 || res.status === 502
                ? 'The document could not be fetched. Please try again shortly.'
                : `The document could not be opened (${res.status}).`,
            res.status,
            null
        );
    },

    /**
     * Trigger the "last 2 days" ingest. Takes NO parameters on purpose: the window, the cap
     * and the sweep behaviour are all fixed server-side, which is what lets this endpoint be
     * CanEdit rather than admin. See api/.../import-recent.ts.
     */
    async importRecent(): Promise<DocuWareImportRecentResult> {
        const response = await fetch('/api/v1/docuware-invoices/import/recent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();
        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }
        return body.data as DocuWareImportRecentResult;
    },
};

export interface DocuWareImportRecentResult {
    /** True when the cooldown or the kill switch refused the run. `reason` says which. */
    skipped: boolean;
    reason?: string;
    lookbackDays: number;
    from?: string;
    companies: string[];
    inserted?: number;
    updated?: number;
    /** Companies whose run threw. A partial import has to read as partial. */
    failedCompanies?: string[];
}
